import { extractEventDates } from './eventDateUtils';

export type EntitySortComparator = ((a: any, b: any) => number) | 'name' | 'date';

const nameComparator = (a: any, b: any) =>
  (a?.name || a?.entity_name || '').localeCompare(b?.name || b?.entity_name || '');

const getSortDateMs = (entity: any): number | null => {
  const { start } = extractEventDates(entity);
  return start ? start.getTime() : null;
};

const getTodayStartMs = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.getTime();
};

const dateComparator = (a: any, b: any) => {
  const dateA = getSortDateMs(a);
  const dateB = getSortDateMs(b);

  if (dateA !== null && dateB !== null) {
    const todayStartMs = getTodayStartMs();
    const aIsUpcoming = dateA >= todayStartMs;
    const bIsUpcoming = dateB >= todayStartMs;
    if (aIsUpcoming !== bIsUpcoming) return aIsUpcoming ? -1 : 1;
    if (dateA !== dateB) return aIsUpcoming ? dateA - dateB : dateB - dateA;
  }

  if (dateA !== null && dateB === null) return -1;
  if (dateA === null && dateB !== null) return 1;
  return nameComparator(a, b);
};

/**
 * Sort entity IDs using a comparator applied to matching entities.
 * Returns entityIds unchanged when sort or entities are not provided.
 */
export function sortEntityIds(
  entityIds: string[],
  entities?: unknown[],
  sort?: EntitySortComparator,
): string[] {
  if (!sort || !entities || entities.length === 0) return entityIds;

  const entityMap = new Map<string, any>();
  for (const entity of entities) {
    const e = entity as any;
    const id = e?.entity_id || e?.id;
    if (id) entityMap.set(id, e);
  }

  const comparators: Record<string, (a: any, b: any) => number> = { name: nameComparator, date: dateComparator };
  const comparator = typeof sort === 'string' ? comparators[sort] : sort;

  return [...entityIds].sort((idA, idB) => {
    const a = entityMap.get(idA);
    const b = entityMap.get(idB);
    if (!a || !b) return 0;
    return comparator(a, b);
  });
}
